import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Check } from "lucide-react";
import { ParticleBackground } from "@/components/ParticleBackground";
import { useCart } from "@/contexts/CartContext";
import { useToast } from "@/hooks/use-toast";
import Page from "@/components/layout/Page";
import { getProductImage, getProductDescription, getProductPrice } from "@/utils/productHelpers";
import { SEOHead } from "@/components/seo/SEOHead";
import { ProductSchema, BreadcrumbSchema } from "@/components/seo/StructuredData";

export default function FidgetSquares() {
  const { addItem } = useCart();
  const { toast } = useToast();
  
  // Sync data from Shop page
  const productName = "Fidget Squares";
  const productImage = getProductImage(productName);
  const productDescription = getProductDescription(productName);
  const productPrice = getProductPrice(productName);

  const handleAddToCart = () => {
    // Redirect to shop - this page showcases the category, specific products are in Shop
    toast({
      title: "View our range",
      description: "Browse our selection of Library Squares in the Shop - perfect as fidget squares!",
    });
  };

  return (
    <>
      <SEOHead
        title="Fidget Squares | Portable Sensory Tools for Focus & Anxiety | Samboards"
        description="Compact tactile Fidget Squares for stress relief, focus & breaking bad habits. Perfect for meetings, classrooms, ADHD, autism & workplace wellness. UK handmade."
        canonical="https://samboards.co.uk/fidget-squares"
        keywords="fidget squares, portable sensory tools, stress relief tools, ADHD fidget tools, autism sensory tools, meeting room fidgets, classroom focus tools, habit breaking, anxiety relief, handmade UK"
        ogImage={productImage.src}
      />
      <ProductSchema
        name="Fidget Squares"
        description={productDescription}
        price={productPrice || 4.60}
        image={productImage.src}
      />
      <BreadcrumbSchema
        items={[
          { name: "Home", url: "/" },
          { name: "Shop", url: "/shop" },
          { name: "Fidget Squares", url: "/fidget-squares" }
        ]}
      />
      
      <div className="min-h-screen overflow-hidden bg-gradient-to-b from-[#020817] via-[#04151F] to-[#020817] text-slate-50">
        <ParticleBackground className="absolute inset-0 -z-10" />
      
      <Page>
        <section className="pb-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            <div className="group relative">
              <div className="relative rounded-3xl bg-gradient-to-br from-slate-800/40 to-slate-900/60 backdrop-blur-xl border border-white/10 p-8 shadow-[0_8px_32px_rgba(0,0,0,0.4)] transition-all duration-300 group-hover:shadow-[0_0_40px_rgba(16,185,129,0.3)] group-hover:border-emerald-400/30">
                <img
                  src={productImage.src}
                  alt={productImage.alt}
                  className="w-full h-auto rounded-2xl"
                />
              </div>
            </div>

            <div className="space-y-8">
              <div>
                <h1 className="text-[40px] sm:text-[48px] lg:text-[54px] leading-[1.1] font-bold mb-4 page-title-gradient">Fidget Squares</h1>
                <p className="text-lg text-slate-200/80 leading-relaxed">
                  {productDescription}
                </p>
              </div>

              <div className="space-y-4">
                <h3 className="text-2xl font-semibold text-slate-100">Perfect For</h3>
                <ul className="space-y-3">
                  {[
                    "Pocket-sized portability",
                    "Meeting and conference rooms",
                    "Classroom focus support",
                    "Stress relief anywhere",
                    "Building better habits"
                  ].map((benefit, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                      <span className="text-base text-slate-300/80">{benefit}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="rounded-3xl bg-gradient-to-br from-slate-800/40 to-slate-900/60 backdrop-blur-xl border border-white/10 p-6 shadow-[0_8px_32px_rgba(0,0,0,0.3)] space-y-4">
                <p className="text-3xl font-bold text-slate-50">From £4.60</p>
                <Button 
                  asChild
                  size="lg" 
                  className="w-full bg-gradient-to-r from-emerald-400 to-teal-400 text-slate-900 hover:from-emerald-300 hover:to-teal-300 rounded-full shadow-[0_0_40px_rgba(16,185,129,0.3)] hover:shadow-[0_0_55px_rgba(16,185,129,0.5)] transition-all"
                >
                  <Link to="/checkout?product=Fidget%20Squares">Buy Now</Link>
                </Button>
              </div>
            </div>
          </div>
        </section>
      </Page>
    </div>
    </>
  );
}
